import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { WaitlistCTA } from './WaitlistCTA';
import { useSuppressFloatingCta } from './floatingCtaContext';
// A deliberately simple three-step row: a single bordered panel split into
// three cells by hairline dividers, each with a small numbered eyebrow
// (01 / 02 / 03 with an orange tick), a short headline, and a line of body.
// No card mockups or per-step visuals — the steps speak for themselves.
const STEPS = [{
  n: '01',
  title: 'Connect',
  body: 'Add Giga to Slack or Teams, then link the tools you already run on — one-click OAuth, no admin.'
}, {
  n: '02',
  title: 'Ask',
  body: 'Say what you want in plain English — the outcome, not the steps. Giga figures out the rest.'
}, {
  n: '03',
  title: 'Act',
  body: 'Giga does the work across your stack and reports back — once, or on a schedule.'
}];
export function GettingStartedSteps() {
  // While this section's own "Get started for free" CTA is on screen, suppress
  // the global floating "Try Giga for free" button so the two don't stack /
  // overlap on mobile.
  const ctaRef = useRef<HTMLDivElement>(null);
  const ctaInView = useInView(ctaRef, {
    margin: '0px 0px -10% 0px'
  });
  useSuppressFloatingCta(ctaInView);
  return <section className="w-full bg-[#FAF8F4] py-24 md:py-32 overflow-hidden" data-id="element-1238">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-1239">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16" data-id="element-1240">
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-5 md:mb-6" data-id="element-1241">
            Up and running in 2 minutes
          </h2>
          <p className="text-base md:text-lg text-neutral-500 font-medium leading-relaxed max-w-2xl mx-auto" data-id="element-1242">
            No setup project. No IT ticket. No training videos.
          </p>
        </div>

        {/* Steps — one bordered panel, three cells with hairline dividers. */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="rounded-2xl md:rounded-[2rem] border border-neutral-200/70 bg-white/40 grid grid-cols-1 md:grid-cols-3 mb-14 md:mb-16 overflow-hidden" data-id="element-1243">
          {STEPS.map((step, i) => <div key={step.n} className={`p-8 md:p-12 ${i < STEPS.length - 1 ? 'border-b md:border-b-0 md:border-r border-neutral-200/70' : ''}`} data-id="element-1244">
              {/* Numbered eyebrow with the orange tick. */}
              <div className="flex items-center gap-3 mb-6" data-id="element-1245">
                <span className="font-mono text-sm font-bold tracking-[0.2em] text-[#EA580C]" data-id="element-1246">
                  {step.n}
                </span>
                <span aria-hidden="true" className="h-px w-7 bg-[#EA580C]" data-id="element-1247" />
              </div>

              <h3 className="text-2xl font-title font-medium text-[#2B211A] tracking-tight leading-tight mb-4" data-id="element-1248">
                {step.title}
              </h3>
              <p className="text-neutral-500 leading-relaxed text-sm md:text-base font-medium max-w-xs" data-id="element-1249">
                {step.body}
              </p>
            </div>)}
        </motion.div>

        {/* CTA */}
        <div ref={ctaRef} className="flex flex-col items-center gap-4" data-id="element-1250">
          <WaitlistCTA className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-[#EA580C] px-8 py-4 text-base font-bold text-white shadow-lg shadow-[#EA580C]/20 transition-all duration-300 hover:bg-[#d24e09] hover:scale-105" data-id="element-1251">
            Get started for free
            <ArrowRight size={18} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-0.5" data-id="element-1252" />
          </WaitlistCTA>
          <p className="text-sm font-medium text-neutral-400" data-id="element-1253">
            Teams is coming soon.
          </p>
        </div>
      </div>
    </section>;
}